import { Button } from '@heroui/react'
import { useFieldArray } from 'react-hook-form'
import type { Control } from 'react-hook-form'

import { NumberFieldControl, SelectFieldControl } from '@/components/ui/form-fields'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'

import type { ProductFormValues } from '../schemas'
import type { ChoiceGroupSummary } from '../types'
import { translated } from '../types'

interface ProductChoiceGroupsFieldProps {
  control: Control<ProductFormValues>
  choiceGroups: ChoiceGroupSummary[]
  isDisabled?: boolean
}

/**
 * The add-on groups a customer picks from when ordering this product — sauces,
 * sizes of a side, whatever the group holds.
 *
 * The group itself is defined once for the tenant; what belongs to the product is
 * how many of its items this product asks for, which is why min and max are edited
 * here rather than on the group.
 */
export function ProductChoiceGroupsField({
  control,
  choiceGroups,
  isDisabled,
}: ProductChoiceGroupsFieldProps) {
  const { fields, append, remove } = useFieldArray({ control, name: 'choiceGroups' })

  const options = choiceGroups.map((group) => ({
    value: group.id,
    label: translated(group.name),
  }))

  return (
    <FormSection
      title="Add-ons"
      description="Choice groups offered with this product, and how many of each it asks for."
      action={
        <Button
          variant="ghost"
          size="sm"
          isDisabled={isDisabled || options.length === 0}
          onPress={() => append({ choiceGroupId: '', minSelect: 1, maxSelect: 1 })}
          className="border-border h-8 shrink-0 rounded-full border px-3 text-xs"
        >
          <PlusIcon width={14} height={14} />
          Add group
        </Button>
      }
    >
      {options.length === 0 ? (
        <FormSectionEmpty>
          No choice groups exist yet. Create one before attaching it to a product.
        </FormSectionEmpty>
      ) : fields.length === 0 ? (
        <FormSectionEmpty>None attached.</FormSectionEmpty>
      ) : (
        <ul className="divide-border border-border divide-y border-t">
          {fields.map((field, index) => (
            <li key={field.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start">
              <div className="flex-1">
                <SelectFieldControl
                  control={control}
                  name={`choiceGroups.${index}.choiceGroupId`}
                  label="Group"
                  options={options}
                  placeholder="Pick a group"
                  isDisabled={isDisabled}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 sm:w-52">
                <NumberFieldControl
                  control={control}
                  name={`choiceGroups.${index}.minSelect`}
                  label="Min"
                  minValue={1}
                  step={1}
                  isDisabled={isDisabled}
                />
                <NumberFieldControl
                  control={control}
                  name={`choiceGroups.${index}.maxSelect`}
                  label="Max"
                  minValue={1}
                  step={1}
                  isDisabled={isDisabled}
                />
              </div>

              <Button
                variant="ghost"
                isIconOnly
                size="sm"
                aria-label={`Remove choice group ${index + 1}`}
                isDisabled={isDisabled}
                onPress={() => remove(index)}
                className="text-muted hover:text-danger size-9 shrink-0 sm:mt-6"
              >
                <TrashIcon width={16} height={16} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </FormSection>
  )
}
